import React, {
    FC,
    useEffect,
    useRef,
    useState
}                               from 'react';
import ReactPlayer              from 'react-player'
import {Popover, VBox}          from '@sirius/ui-lib/src/blocks/Layout';
import { IconButtonColored }    from '@sirius/ui-lib/src/blocks/Controls/Button';
import {Chips, ChipsList}       from '@sirius/ui-lib/src/blocks/Controls/Chips';
import {IVideoContentPlayer}    from './@types';
import {
    block,
    EXTERNAL_IFRAME_ALLOW,
    NO_DOWNLOAD_ATTRIBUTES,
    PLAYBACK_RATE
}                               from './block';
import {
    getEmbedUrl,
    getVideoType,
    isExternalVideoType
}                               from './helpers';
import                               './index.less';


const stopPropagation = (e: React.MouseEvent) => e && e.stopPropagation();


const VideoPlayer: FC<IVideoContentPlayer> = (
    {
        videoContainer,
        controls           = true,
        loop               = false,
        pip                = false,
        disableDownload    = false,
        disableContextMenu = false,
        className          = '',
        onError,
    }
) => {
    const {url} = videoContainer;
    const type  = getVideoType(videoContainer);

    const isStorageVideo  = type === 'storage';
    const isExternalVideo = isExternalVideoType(type);

    const embedUrl = isExternalVideo ? getEmbedUrl(type, url) : null;
    const config   = disableDownload ? NO_DOWNLOAD_ATTRIBUTES : {};

    const [playbackRate, setPlaybackRate] = useState(PLAYBACK_RATE.normal.value);
    const [target, setTarget]             = useState(null);

    const onErrorRef   = useRef(onError);
    onErrorRef.current = onError;

    useEffect(() => {
        if (isExternalVideo && !embedUrl) {
            const error = new Error(`Unsupported ${type} video url: ${url}`);
            onErrorRef.current?.(error);
        }
    }, [isExternalVideo, embedUrl, type, url]);

    const toggleMenu = (e: React.MouseEvent): void => {
        stopPropagation(e);
        setTarget(!!target ? null : e.currentTarget);
    };

    const closeMenu = () => setTarget(null);

    const contextMenuHandler = (e: React.MouseEvent) => disableContextMenu && e.preventDefault();

    const onChangeChipsHandler = (next: any) => {
        setPlaybackRate(next);
        closeMenu();
    };

    const externalPlayer = embedUrl
        ? <iframe className       = {'react-player'}
                  data-video      = {type}
                  title           = {type}
                  src             = {embedUrl}
                  allow           = {EXTERNAL_IFRAME_ALLOW}
                  loading         = {'lazy'}
                  allowFullScreen
            />
        : null
    ;
    
    return (
        <div className          = {`${className} ` + block.mod('type', type)}
             data-video-wrapper = {type}
        >
            { isExternalVideo
                ? externalPlayer
                : <ReactPlayer className     = {'react-player'}
                               url           = {url}
                               controls      = {controls}
                               config        = {config}
                               loop          = {loop}
                               pip           = {pip}
                               width         = {'100%'}
                               height        = {'100%'}
                               data-video    = {type}
                               onContextMenu = {contextMenuHandler}
                               playbackRate  = {playbackRate}
                               onError       = {onError}
                    />
            }
            { isStorageVideo &&
                <VBox className = {block.el('menu')}>
                    <IconButtonColored icon     = {'speed'}
                                       size     = {'s'}
                                       palette  = {'grayscale'}
                                       onAction = {toggleMenu}
                                       active   = {!!target}
                    />
                    <Popover target     = {target}
                             onClose    = {closeMenu}
                             placement  = {'bottom-end'}
                             offset     = {[-2, 4]}
                    >
                        <ChipsList direction           = {'vertical'}
                                   align               = {'center'}
                                   selected            = {[playbackRate]}
                                   onChange            = {onChangeChipsHandler}
                        >
                            {   Object.values(PLAYBACK_RATE).map(
                                    ({label, value}, ci) =>
                                        <Chips key         = {ci}
                                               size        = {'xxs'}
                                               value       = {value}
                                        >
                                            {label ?? value}
                                        </Chips>
                                )
                            }
                        </ChipsList>
                    </Popover>
                </VBox>
            }

        </div>

    )
};

export default VideoPlayer;
